import * as overviewActions from './actions';
import * as honoraryNoteLogic from '../../../business/logic/honorary-note-logic';

import {
  CommunicationOverviewDocumentItem,
  CommunicationOverviewDocumentItemForDisplay,
  CommunicationOverviewFreeTextItemForDisplay,
  CommunicationOverviewHonoraryNoteItem,
  CommunicationOverviewHonoraryNoteItemForDisplay,
  CommunicationOverviewReportItemForDisplay,
  CommunicationOverviewRimItem,
  CommunicationOverviewRimItemForDisplay,
  CommunicationOverviewSvsItem,
  CommunicationOverviewSvsItemForDisplay,
  DocumentTypeTC,
  OverviewActionTypes,
  OverviewState,
  communicationOverviewStatus
} from './types';
import { DetailsHonoraryNoteState, HonoraryNote } from '../honorary-note/types';
import { DetailsRimState, RimTotal } from '../rim/types';
import { DetailsSvsState, SvsItem } from '../svs/types';

import { ActionType } from 'typesafe-actions';
import { CommentOrReport } from '../comment-or-report/model';
import { DetailsCommentOrReportState } from '../comment-or-report/types';
import { DetailsImagesState } from '../images/types';
import { DetailsOtherAttachmentsState } from '../other-attachments/types';
import { Image } from '../images/model';
import { OtherAttachment } from '../other-attachments/model';
import { Reducer } from 'redux';
import { ReportStatusTC } from '../../../business/models/typecode.enums';
import { SharedState } from '../../shared/types';
import { TypeCodeTranslation } from '../../../business/models/shared.model';
import { createSelector } from 'reselect';

const initialState: OverviewState = {
  rimList: [],
  svsList: [],
  freeTextList: [],
  documentList: [],
  reportList: [],
  honoraryNoteList: [],
  rimSelectedIds: [],
  svsSelectedIds: [],
  honoraryNoteSelectedIds: [],
  freeTextSelectedIds: [],
  documentSelectedIds: [],
  reportSelectedIds: [],
  rimNonValidIds: [],
  svsNonValidIds: [],
  rimNonValidBusinessList: [],
  svsNonValidBusinessList: [],

  toggleAllRimIsChecked: false,
  toggleAllSvsIsChecked: false,
  toggleAllFreeTextIsChecked: false,
  toggleAllImageIsChecked: false,
  toggleAllReportIsChecked: false,
  toggleAllDaccaRimIsChecked: false,
  toggleAllDaccaSvsIsChecked: false,
  toggleAllHonoraryNoteIsChecked: false,
  showAskedAmountModal: false,
  showDeleteItemsModal: false,
  askedAmount: ''
};

// Non-memoized selectors - move these to special file inside rim folder eg. reducer_selectors!?
const getCurrentUserLanguage = (state: SharedState) => {
  return state.currentLanguage;
};

const getRimList = (state: DetailsRimState) => {
  return state.rimsTotal;
};

const getRimStatusList = (state: DetailsRimState) => {
  return state.rimTypeCodes.rimStatusList;
};

const getSvsList = (state: DetailsSvsState) => {
  return state.svsItems;
};

const getFreeTextList = (state: DetailsCommentOrReportState) => {
  return state.commentsOrReports.filter(x => x.id && x.reportStatusTC === ReportStatusTC.FreeText);
};

const getReportList = (state: DetailsCommentOrReportState) => {
  return state.commentsOrReports.filter(x => x.id && x.reportStatusTC !== ReportStatusTC.FreeText);
};

const getReportTypeList = (state: DetailsCommentOrReportState) => {
  return state.documentTypeTCValues;
};


const getImageList = (state: DetailsImagesState) => {
  return state.images;
};

const getOtherAttachmentList = (state: DetailsOtherAttachmentsState) => {
  return state.otherAttachments;
};

const getHonoraryNoteList = (state: DetailsHonoraryNoteState) => {
  return state.honoraryNotes;
};

const getCommunicationOverviewRimList = (state: OverviewState) => state.rimList;
const getCommunicationOverviewSvsList = (state: OverviewState) => state.svsList;
const getCommunicationOverviewDocumentList = (state: OverviewState) => state.documentList;
const getCommunicationOverviewFreeTextList = (state: OverviewState) => state.freeTextList;
const getCommunicationOverviewReportList = (state: OverviewState) => state.reportList;
const getCommunicationOverviewHonoraryNoteList = (state: OverviewState) => state.honoraryNoteList;

const communicationOverviewSvsSelectedIds = (state: OverviewState) => state.svsSelectedIds;
const communicationOverviewRimSelectedIds = (state: OverviewState) => state.rimSelectedIds;
const communicationOverviewDocumentSelectedIds = (state: OverviewState) => state.documentSelectedIds;
const communicationOverviewFreeTextSelectedIds = (state: OverviewState) => state.freeTextSelectedIds;
const communicationOverviewReportSelectedIds = (state: OverviewState) => state.reportSelectedIds;
const communicationOverviewHonoraryNoteSelectedIds = (state: OverviewState) => state.honoraryNoteSelectedIds;
const communicationOverviewRimNonValidIds = (state: OverviewState) => state.rimNonValidIds;
const communicationOverviewSvsNonValidIds = (state: OverviewState) => state.svsNonValidIds;

export const getToggleAllRimIsChecked = (state: OverviewState) => state.toggleAllRimIsChecked;
export const getToggleAllSvsIsChecked = (state: OverviewState) => state.toggleAllSvsIsChecked;
export const getToggleAllDaccaRimIsChecked = (state: OverviewState) => state.toggleAllDaccaRimIsChecked;
export const getToggleAllDaccaSvsIsChecked = (state: OverviewState) => state.toggleAllDaccaSvsIsChecked;
export const getToggleAllFreeTextIsChecked = (state: OverviewState) => state.toggleAllFreeTextIsChecked;
export const getToggleAllImageIsChecked = (state: OverviewState) => state.toggleAllImageIsChecked;
export const getToggleAllReportIsChecked = (state: OverviewState) => state.toggleAllReportIsChecked;
export const getToggleAllHonoraryNoteIsChecked = (state: OverviewState) => state.toggleAllHonoraryNoteIsChecked;

// Memoized selectors
export const getCommunicationOverviewRimListForDisplay = createSelector(
  [getRimList, getCommunicationOverviewRimList, communicationOverviewRimSelectedIds, communicationOverviewRimNonValidIds, getRimStatusList],
  (rimList: RimTotal[], sentList: CommunicationOverviewRimItem[], selectedList: number[], nonValidIds: number[], rimStatusList: TypeCodeTranslation[]) => {
    return rimList.map(item => {
      const sentItem = sentList.filter(x => x.id === item.rimHeaderId)[0];
      return new CommunicationOverviewRimItemForDisplay(sentItem,
        item,
        rimStatusList,
        selectedList.includes(item.rimHeaderId),
        !nonValidIds.includes(item.rimHeaderId)
        );
    });
  });

export const getCommunicationOverviewSvsListForDisplay = createSelector(
  [getSvsList, getCommunicationOverviewSvsList, communicationOverviewSvsSelectedIds, communicationOverviewSvsNonValidIds],
  (svsList: SvsItem[], sentList: CommunicationOverviewSvsItem[], selectedList: number[], nonValidIds: number[]) => {
    return svsList.map(item => {
      const sentItem = sentList.filter(x => x.id === item.id)[0];
      return new CommunicationOverviewSvsItemForDisplay(sentItem, item,
        selectedList.includes(item.id),
        !nonValidIds.includes(item.id));
    });
  });

export const getCommunicationOverviewFreeTextListForDisplay = createSelector(
  [getFreeTextList, getCommunicationOverviewFreeTextList, communicationOverviewFreeTextSelectedIds],
  (freeTextList: CommentOrReport[], sentList: CommunicationOverviewDocumentItem[], selectedList: number[]) => {
    return freeTextList.map(item => {
      const sentItem = sentList.filter(x => x.id === item.id)[0];
      return new CommunicationOverviewFreeTextItemForDisplay(sentItem, item, selectedList.includes(item.id));
    });
  });

export const getCommunicationOverviewImageListForDisplay = createSelector(
  [getImageList, getCommunicationOverviewDocumentList, communicationOverviewDocumentSelectedIds],
  (imageList: Image[], sentList: CommunicationOverviewDocumentItem[], selectedList: number[]) => {
    const imageResult = imageList.map(item => {
      const sentItem = sentList.filter(x => x.id === item.id)[0];
      return new CommunicationOverviewDocumentItemForDisplay(sentItem, item, selectedList.includes(item.id));
    });

    return imageResult;
  });

export const getCommunicationOverviewOtherAttachmentListForDisplay = createSelector(
  [getOtherAttachmentList, getCommunicationOverviewDocumentList, communicationOverviewDocumentSelectedIds],
  (otherAttachmentList: OtherAttachment[], sentList: CommunicationOverviewDocumentItem[], selectedList: number[]) => {
    const otherAttachmentResult = otherAttachmentList.map(item => {
      const sentItem = sentList.filter(x => x.id === item.id)[0];
      return new CommunicationOverviewDocumentItemForDisplay(sentItem, item, selectedList.includes(item.id));
    });

    return otherAttachmentResult;
  });


export const getCommunicationOverviewReportListForDisplay = createSelector(
  [getReportList, getCommunicationOverviewReportList, getReportTypeList, communicationOverviewReportSelectedIds, getCurrentUserLanguage],
  (reportList: CommentOrReport[], sentList: CommunicationOverviewDocumentItem[], reportTypeList: TypeCodeTranslation[], selectedList: number[], currentUserLanguage: string) => {
    return reportList.map(item => {
      const sentItem = sentList.filter(x => x.id === item.id)[0];
      return new CommunicationOverviewReportItemForDisplay(sentItem, item, reportTypeList, selectedList.includes(item.id), currentUserLanguage);
    });
  });

export const getCommunicationOverviewHonoraryNoteListForDisplay = createSelector(
  [getHonoraryNoteList, getCommunicationOverviewHonoraryNoteList, communicationOverviewHonoraryNoteSelectedIds],
  (honoraryNoteList: HonoraryNote[], sentList: CommunicationOverviewHonoraryNoteItem[], selectedList: number[]) => {
    return honoraryNoteList.map(item => {
      const sentItem = sentList.filter(x => x.id === item.honoraryNoteId)[0];
      const amount = honoraryNoteLogic.getTotal(item.honoraryNoteLines);
      return new CommunicationOverviewHonoraryNoteItemForDisplay(sentItem, item, selectedList.includes(item.honoraryNoteId), amount);
    });
  });


// Reducer
export type OverviewAction = ActionType<typeof overviewActions>;

const reducer: Reducer<OverviewState, OverviewAction> = (state = initialState, action): OverviewState => {
  switch (action.type) {
    case OverviewActionTypes.RIM_STATUS_CHANGE:
      return { ...state, rimNonValidIds: [] };

    case OverviewActionTypes.TOGGLE_RIM_ITEM: {
      const item = state.rimList.find(item => item.id === action.payload);
      if (item && item.status === communicationOverviewStatus.sent) {
        return state;
      }
    
      const rimSelectedIds = state.rimSelectedIds.includes(action.payload)
        ? state.rimSelectedIds.filter(id => id !== action.payload)
        : [...state.rimSelectedIds, action.payload];
    
      return { ...state, rimSelectedIds };
    }
    case OverviewActionTypes.TOGGLE_ALL_RIM_ITEM: {
      const rimSelectedIds = action.payload
        ? state.rimList.filter(item => item.status !== communicationOverviewStatus.sent).map(item => item.id)
        : [];
      return {
        ...state,
        toggleAllRimIsChecked: action.payload,
        rimSelectedIds: rimSelectedIds
      };
    }
    case OverviewActionTypes.TOGGLE_SVS_ITEM:
      const itemSvs = state.svsList.find(item => item.id === action.payload);
      if (itemSvs && itemSvs.status === communicationOverviewStatus.sent) {
        return state;
      }

      const svsSelectedIds = state.svsSelectedIds.includes(action.payload)
        ? state.svsSelectedIds.filter(id => id !== action.payload)
        : [...state.svsSelectedIds, action.payload];

      return { ...state, svsSelectedIds };
    case OverviewActionTypes.TOGGLE_ALL_SVS_ITEM: {
      const svsSelectedIds = action.payload
        ? state.svsList.filter(item => item.status !== communicationOverviewStatus.sent).map(item => item.id)
        : [];
      return {
        ...state, 
        toggleAllSvsIsChecked: action.payload,
        svsSelectedIds: svsSelectedIds
      };
    }
    case OverviewActionTypes.TOGGLE_FREE_TEXT_ITEM:
      const itemFreeText = state.freeTextList.find(item => item.id === action.payload);
      if (itemFreeText && itemFreeText.status === communicationOverviewStatus.sent) {
        return state;
      }
      
      const freeTextSelectedIds = state.freeTextSelectedIds.includes(action.payload)
        ? state.freeTextSelectedIds.filter(id => id !== action.payload)
        : [...state.freeTextSelectedIds, action.payload];

      return { ...state, freeTextSelectedIds };
    case OverviewActionTypes.TOGGLE_ALL_FREE_TEXT_ITEM: {
      const freeTextSelectedIds = action.payload
        ? state.freeTextList.filter(item => item.status !== communicationOverviewStatus.sent).map(item => item.id)
        : [];
      return {
        ...state, 
        toggleAllFreeTextIsChecked: action.payload,
        freeTextSelectedIds: freeTextSelectedIds
      };
    }
    case OverviewActionTypes.TOGGLE_IMAGE_ITEM:
      const itemImage = state.documentList.find(item => item.id === action.payload);
      if (itemImage && itemImage.status === communicationOverviewStatus.sent) {
        return state;
      }
      
      const documentSelectedIds = state.documentSelectedIds.includes(action.payload)
        ? state.documentSelectedIds.filter(id => id !== action.payload) 
        : [...state.documentSelectedIds, action.payload];

      return { ...state, documentSelectedIds};
    case OverviewActionTypes.TOGGLE_ALL_IMAGE_ITEM: {
      const documentSelectedIds = action.payload
        ? state.documentList.filter(item => item.status !== communicationOverviewStatus.sent).map(item => item.id)
        : [];
      return {
        ...state, 
        toggleAllImageIsChecked: action.payload,
        documentSelectedIds: documentSelectedIds
      };
    }
    case OverviewActionTypes.TOGGLE_REPORT_ITEM:
      const reportItemToToggle = (state.reportList.filter(item => item.id === action.payload));
      if (reportItemToToggle.length > 0 && reportItemToToggle[0].status === communicationOverviewStatus.sent) { return state; }

      if (state.reportSelectedIds.includes(action.payload)) {
        return { ...state, reportSelectedIds: state.reportSelectedIds.filter(item => item !== action.payload) };
      }
      return { ...state, reportSelectedIds: [...state.reportSelectedIds, action.payload] };
    case OverviewActionTypes.TOGGLE_ALL_REPORT_ITEM: {
      const reportSelectedIds = action.payload
        ? state.reportList.filter(item => item.status !== communicationOverviewStatus.sent).map(item => item.id)
        : [];
      return {
        ...state, 
        toggleAllReportIsChecked: action.payload,
        reportSelectedIds: reportSelectedIds
      };
    }
    case OverviewActionTypes.TOGGLE_HONORARY_NOTE_ITEM:
      const honoraryNoteItemToToggle = (state.honoraryNoteList.filter(item => item.id === action.payload));
      if (honoraryNoteItemToToggle.length > 0 && honoraryNoteItemToToggle[0].status === communicationOverviewStatus.sent) { return state; }

      if (state.honoraryNoteSelectedIds.includes(action.payload)) {
        return { ...state, honoraryNoteSelectedIds: state.honoraryNoteSelectedIds.filter(item => item !== action.payload) };
      }
      return { ...state, honoraryNoteSelectedIds: [...state.honoraryNoteSelectedIds, action.payload] };
    case OverviewActionTypes.TOGGLE_SELECT_ALL: {
      if (!action.payload.checked) {
        return {
          ...state,
          freeTextSelectedIds: [],
          reportSelectedIds: [],
          documentSelectedIds: [],
          svsSelectedIds: [],
          rimSelectedIds: [],
          honoraryNoteSelectedIds: [],
        };
      }
    
      const filterItems = (list: any[]) =>
        list.filter(item => item.canSend() === true).map(item => item.id());
    
      return {
        ...state,
        rimSelectedIds: filterItems(action.payload.rimList),
        svsSelectedIds: filterItems(action.payload.svsList),
        documentSelectedIds: filterItems(action.payload.documentList),
        reportSelectedIds: filterItems(action.payload.reportList),
        freeTextSelectedIds: filterItems(action.payload.freeTextList),
        honoraryNoteSelectedIds: filterItems(action.payload.honoraryNoteList),
      };
    }
    case OverviewActionTypes.GET_RIM_LIST_SUCCESS: {
      const rimList = action.payload.map(item => ({
        id: item.id,
        status: item.sent ? communicationOverviewStatus.sent : communicationOverviewStatus.open,
        sentOn: item.sentOn,
      }));
    
      return { ...state, rimList, rimSelectedIds: [], toggleAllRimIsChecked: false };
    }
    case OverviewActionTypes.GET_SVS_LIST_SUCCESS: {
      const svsList: CommunicationOverviewSvsItem[] = action.payload.map(item => ({
        id: item.id,
        status: item.sent ? communicationOverviewStatus.sent : communicationOverviewStatus.open,
        sentOn: item.sentOn,
      }));
    
      return {
        ...state,
        svsList,
        svsSelectedIds: [],
        toggleAllSvsIsChecked: false,
      };
    }
    
    case OverviewActionTypes.GET_HONORARY_NOTE_LIST_SUCCESS: {
      const honoraryNoteList: CommunicationOverviewHonoraryNoteItem[] = action.payload.map(item => ({
        id: item.id,
        status: item.sent ? communicationOverviewStatus.sent : communicationOverviewStatus.open,
        sentOn: item.sentOn,
        messageTypeTC: item.messageTypeTC,
        sent: item.sent,
        amount: item.amount,
      }));
    
      return {
        ...state,
        honoraryNoteList,
        honoraryNoteSelectedIds: [],
        toggleAllHonoraryNoteIsChecked: false,
      };
    }
    case OverviewActionTypes.SVS_SET_NON_VALID_ITEMS: {
      return { ...state, svsNonValidIds: action.payload.map(id => id) };
    }
    case OverviewActionTypes.RIM_SET_NON_VALID_ITEMS: {
      return { ...state, rimNonValidIds: action.payload.map(id => id) };
    }
    case OverviewActionTypes.SVS_SET_NON_VALID_BUSINESS_ITEMS: {
      return { ...state, svsNonValidBusinessList: action.payload.map(item => item) };
    }
    case OverviewActionTypes.RIM_SET_NON_VALID_BUSINESS_ITEMS: {
      return { ...state, rimNonValidBusinessList: action.payload.map(item => item) };
    }
    case OverviewActionTypes.GET_DOCUMENT_LIST_SUCCESS: {
      const docItems = action.payload.map(x => {
        const item: CommunicationOverviewDocumentItem = {
          id: x.documentId,
          sentOn: x.sentOn,
          documentType: messageTypeToDocumentType(x.messageTypeTC),
          status: x.documentId === 0 ? communicationOverviewStatus.open : communicationOverviewStatus.sent,
          reportStatusTC: x.reportStatusTC,
        };

        return item;
      });
      return {
        ...state,
        documentList: docItems.filter(x => x.documentType === DocumentTypeTC.image || x.documentType === DocumentTypeTC.otherAttachment),
        freeTextList: docItems.filter(x => x.documentType === DocumentTypeTC.commentOrReport && x.reportStatusTC === 'FreeText'),
        reportList: docItems.filter(x => x.documentType === DocumentTypeTC.commentOrReport && x.reportStatusTC !== 'FreeText'),
        documentSelectedIds: [],
        freeTextSelectedIds: [],
        reportSelectedIds: [],
        toggleAllFreeTextIsChecked: false,
        toggleAllImageIsChecked: false,
        toggleAllReportIsChecked: false
      };
    }
    case OverviewActionTypes.GET_ASKED_AMOUNT_SUCCESS: {
      return { ...state, askedAmount: action.payload.askedAmount };
    }
    case OverviewActionTypes.SET_ASKED_AMOUNT_SUCCESS: {
      return { ...state, askedAmount: action.payload };
    }
    case OverviewActionTypes.CHANGE_ASKED_AMOUNT: {
      return { ...state, askedAmount: action.payload.amount };
    }
    case OverviewActionTypes.ASKED_AMOUNT_MODAL_OPEN: {
      return { ...state, showAskedAmountModal: true };
    }
    case OverviewActionTypes.ASKED_AMOUNT_MODAL_CLOSE: {
      return { ...state, showAskedAmountModal: false };
    }
    case OverviewActionTypes.DELETE_OVERVIEW_MODAL_OPEN: {
      return { ...state,  showDeleteItemsModal: true };
    }
    case OverviewActionTypes.DELETE_OVERVIEW_MODAL_CLOSE: {
      return { ...state,  showDeleteItemsModal: false };
    }


    case OverviewActionTypes.GENERATE_REPORTS_RIM_FAIL:
    case OverviewActionTypes.GENERATE_REPORTS_SVS_FAIL:
    default:
      return state;
  }
};

function messageTypeToDocumentType(msg: string) {
  switch (msg) {
    case 'Image': return DocumentTypeTC.image;
    case 'OtherAttachment': return DocumentTypeTC.otherAttachment;
    case 'DaccaCalculationDIY': return DocumentTypeTC.daccaCalculationDIY; // CHECK IF THIS IS OBSOLETE!
    case 'DaccaCalculationFIN': return DocumentTypeTC.daccaCalculationFIN; // CHECK IF THIS IS OBSOLETE!
    case 'DaccaCalculationRIK': return DocumentTypeTC.daccaCalculationRIK; // CHECK IF THIS IS OBSOLETE!
  }

  return DocumentTypeTC.commentOrReport;
}

export { reducer as overviewReducer, initialState as overviewInitialState };
